// src/pages/admin/AdminReviews.tsx
//
// The tutor review queue (/admin/reviews). Children's work waits here,
// oldest first; the target is feedback within 48 hours. Tutors see only
// this page; admins see it alongside everything else.

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetReviewQueueQuery,
  useGetReviewStatsQuery,
} from "../../app/api/learningApi";
import { EmptyState, PageHeader, Skeleton, StatCard, trackConfig } from "./AdminShared";

import { AlarmClock, ArrowLeft, ArrowRight, Blocks, Camera, Check, Clapperboard, Hourglass, Link2, Mic, Paperclip, PartyPopper, PenLine, Timer } from "lucide-react";
import type { LucideIcon } from "lucide-react";
const TABS = [
  { status: "submitted", label: "Waiting" },
  { status: "needs_revision", label: "Sent back" },
  { status: "accepted", label: "Accepted" },
] as const;

const TRACKS = [
  { value: "", label: "All ages" },
  { value: "foundation_builders", label: "6–9" },
  { value: "explorers", label: "9–12" },
  { value: "innovators", label: "13–18" },
];

const EVIDENCE_ICON: Record<string, LucideIcon> = {
  photo: Camera, video: Clapperboard, audio: Mic, link: Link2, file: Paperclip, text: PenLine, project: Blocks,
};

function waited(h: number | null) {
  if (h == null) return "";
  if (h < 1) return "just now";
  if (h < 48) return `${h}h waiting`;
  return `${Math.round(h / 24)} days waiting`;
}

export default function AdminReviews() {
  const [status, setStatus] = useState<string>("submitted");
  const [track, setTrack] = useState("");
  const [page, setPage] = useState(1);
  const { data: stats } = useGetReviewStatsQuery(undefined, { pollingInterval: 60_000 });
  const { data, isLoading } = useGetReviewQueueQuery({ status, track, page }, { pollingInterval: 60_000 });
  const s = stats?.data;
  const items = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Reviews"
        subtitle="Children's work waiting for feedback, oldest first. Aim to review everything within 48 hours."
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <StatCard label="Waiting" value={String(s?.waiting ?? "—")} icon={Hourglass} color="#7a4a10" bg="#fff8ee" />
        <StatCard
          label="Over 48 hours"
          value={String(s?.overdue ?? "—")}
          icon={AlarmClock}
          color={s?.overdue ? "#9b2a2a" : "#2a7a56"}
          bg={s?.overdue ? "#fff3f3" : "#edfaf4"}
        />
        <StatCard
          label="Average time to feedback"
          value={s?.averageHoursToReview != null ? `${s.averageHoursToReview}h` : "—"}
          sub="last 30 days"
          icon={Timer}
          color="#4a44b0"
          bg="#ebeff7"
        />
        <StatCard label="Reviewed" value={String(s?.reviewedLast30Days ?? "—")} sub="last 30 days" icon={Check} color="#2a7a56" bg="#edfaf4" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-2xl border border-[#e5e8ef] bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.status}
              onClick={() => { setStatus(t.status); setPage(1); }}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${status === t.status ? "bg-[#1b2d5e] text-white" : "text-[#8189a0] hover:text-[#16213e]"}`}
            >
              {t.label}
              {t.status === "submitted" && s?.waiting ? ` (${s.waiting})` : ""}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {TRACKS.map((t) => (
            <button
              key={t.value}
              onClick={() => { setTrack(t.value); setPage(1); }}
              className={`rounded-xl border px-3 py-1.5 text-[12.5px] font-bold ${track === t.value ? "border-[#1b2d5e] bg-[#f1f3f8] text-[#4a44b0]" : "border-[#e5e8ef] bg-white text-[#8189a0]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-[16px]" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={status === "submitted"  ? PartyPopper : PartyPopper}
          title={status === "submitted" ? "Nothing waiting" : "Nothing here yet"}
          subtitle={status === "submitted" ? "Every piece of work has feedback. Great job." : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-[20px] border border-[#e5e8ef] bg-white">
          {items.map((it) => {
            const tc = trackConfig(it.track);
            return (
              <Link
                key={it.id}
                to={`/admin/reviews/${it.id}`}
                className="flex items-center gap-4 border-b border-[#f7f8fb] px-5 py-3.5 transition last:border-0 hover:bg-[#f9fafc]"
              >
                <span
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold"
                  style={{ background: tc.bg, color: tc.text }}
                >
                  {it.child?.firstName?.[0] ?? "?"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[#16213e]">
                    {it.child ? `${it.child.firstName} ${it.child.lastInitial}. · ${it.child.age}` : "Child"}
                    <span className="font-normal text-[#8189a0]"> — {it.lesson.title ?? it.lesson.key}</span>
                  </p>
                  <p className="text-[12.5px] text-[#8189a0]">
                    {it.weekKey} · try {it.attemptNumber} · <span className="inline-flex items-center gap-1 align-[-0.125em]">
                      {it.evidenceTypes.map((t) => {
                        const Icon = EVIDENCE_ICON[t];
                        return Icon ? <Icon key={t} className="h-3.5 w-3.5" aria-label={t} /> : <span key={t}>{t}</span>;
                      })}
                    </span>
                    {it.reviewerName && ` · reviewed by ${it.reviewerName}`}
                  </p>
                </div>
                {it.status === "submitted" ? (
                  <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-bold ${it.overdue ? "bg-[#fff3f3] text-[#9b2a2a]" : "bg-[#fff8ee] text-[#7a4a10]"}`}>
                    {waited(it.waitingHours)}
                  </span>
                ) : (
                  <span className="flex-shrink-0 text-[12.5px] text-[#8189a0]">
                    {it.reviewedAt ? new Date(it.reviewedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {(data?.pages ?? 1) > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs disabled:opacity-40"><ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Newer</button>
          <span className="px-2 py-1.5 text-xs text-[#8189a0]">Page {page} of {data?.pages}</span>
          <button disabled={page >= (data?.pages ?? 1)} onClick={() => setPage((p) => p + 1)} className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs disabled:opacity-40">Older{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></button>
        </div>
      )}
    </>
  );
}
